// NOTE: Strings in this file describe unbuilt internal roadmap surfaces, are founder-only,
// and are deleted as each capability lands. Per Decision 1a-F6, they are exempt from NLS key extraction.

import { type OwnerRoute, ownerRouteToPath } from '../ownerRouter'
import { useNls } from '../../../nls/nlsProvider'

type RouteSpec = {
  title: string
  chapter: string
  description: string
  plannedFeatures: Array<{ label: string; detail: string }>
}

const getRouteSpec = (route: OwnerRoute): RouteSpec => {
  switch (route.view) {
    case 'people':
    case 'person-detail':
      return {
        title: 'People Hub',
        chapter: 'Chapter 07 · People Hub (Phase 1b)',
        description: 'Comprehensive directory of users across all workspaces with account status, activity stats, plan tier, and owner actions.',
        plannedFeatures: [
          { label: 'User Directory', detail: 'Filterable list by role, signup recency, active sessions, and restriction flags.' },
          { label: 'Personal Credits & Wallet', detail: 'Inspect and grant personal credits directly to user accounts.' },
          { label: 'Session Management', detail: 'Review active device sessions, trigger password reset, or revoke access.' },
        ],
      }
    case 'broadcast':
    case 'broadcast-new':
    case 'broadcast-detail':
      return {
        title: 'Broadcast Center',
        chapter: 'Chapter 14 · Broadcast System (Phase 5)',
        description: 'Send targeted announcements, downtime alerts, and feature updates across banner, email, and push notification channels.',
        plannedFeatures: [
          { label: 'Audience Targeting', detail: 'Segment by workspace plan, role, tenure, or specific feature flag enablement.' },
          { label: 'Multi-channel Delivery', detail: 'Synchronized delivery via in-app persistent banners, web push, and email.' },
          { label: 'Delivery Analytics', detail: 'Impression and click-through rates with per-workspace delivery metrics.' },
        ],
      }
    case 'config-economy':
      return {
        title: 'Economy & Credits Engine',
        chapter: 'Chapter 16 · Credits & Economy Tuning (Phase 7)',
        description: 'Configure credit pricing, model token costs, signup bonuses, curses/perks balance, and system-wide credit defaults.',
        plannedFeatures: [
          { label: 'Signup Bonus Tuning', detail: 'Set initial credits granted to new workspaces and personal spaces.' },
          { label: 'AI Token Multipliers', detail: 'Adjust model credit multipliers based on underlying API cost changes.' },
          { label: 'Gamification Balance', detail: 'Adjust curse durations, raid rewards, and streak multiplier curves.' },
        ],
      }
    case 'config-feedback-authoring':
    case 'config-feedback-authoring-detail':
      return {
        title: 'Survey & Feedback Authoring',
        chapter: 'Chapter 11 · Feedback Authoring (Phase 4)',
        description: 'Author question prompts, targeted surveys, NPS widgets, and contextual in-app feedback sheets.',
        plannedFeatures: [
          { label: 'Question Builder', detail: 'Create single-choice, rating, multi-select, and long-form feedback questions.' },
          { label: 'Trigger Rules', detail: 'Trigger upon milestone completion, export events, or churn risk signals.' },
          { label: 'Multi-lingual Prompts', detail: 'Localized survey variants with fallback to English.' },
        ],
      }
    case 'config-experience-engine':
      return {
        title: 'Experience Engine Tuning',
        chapter: 'Chapter 13 · Experience Engine Tuning (Phase 4)',
        description: 'Manage ambient in-app prompts, proactive tips, cooldown policies, and error-resolution recommendations.',
        plannedFeatures: [
          { label: 'Prompt Registry', detail: 'Audit all registered micro-prompts across desktop and mobile wrappers.' },
          { label: 'Cooldown Caps', detail: 'Enforce weekly limits on prompt frequency to avoid user fatigue.' },
          { label: 'Error Interception', detail: 'Register guided recovery paths for known network or permission errors.' },
        ],
      }
    case 'config-moderation':
      return {
        title: 'Content & Abuse Moderation',
        chapter: 'Chapter 21 · Public Workspaces & Moderation (Phase 8)',
        description: 'Tools for reviewing public workspaces, demo templates, reported abuse, and automated content screening.',
        plannedFeatures: [
          { label: 'Public Workspace Queue', detail: 'Review workspaces submitted for Explore hub publication.' },
          { label: 'Takedown Controls', detail: 'Instantly delist or restrict workspaces violating usage policies.' },
          { label: 'Abuse Audit Feed', detail: 'Flagged suspicious activity, rapid spam creation, or high-risk uploads.' },
        ],
      }
    case 'activity':
    case 'activity-detail':
      return {
        title: 'Owner Activity & Audit Log',
        chapter: 'Chapter 06 · Roles, Claims & Audit Log (Phase 2)',
        description: 'Comprehensive, immutable log of all owner actions, impersonation sessions, credit adjustments, and break-glass events.',
        plannedFeatures: [
          { label: 'Event Filtering', detail: 'Search by actor, target workspace, action kind, and timestamp range.' },
          { label: 'Break-Glass Audit', detail: 'Review emergency content peek justifications and expiration states.' },
          { label: 'Export to CSV / JSON', detail: 'Export signed audit trail records for compliance and accounting.' },
        ],
      }
    default:
      return {
        title: 'Planned Capability',
        chapter: 'Upcoming Revamp Chapter',
        description: 'This capability is part of the Owner Console Revamp roadmap and will be implemented in a subsequent phase.',
        plannedFeatures: [
          { label: 'Configured Route', detail: `Active path: ${ownerRouteToPath(route)}` },
          { label: 'State & Navigation', detail: 'Router and deep-linking contracts are ready for this surface.' },
          { label: 'Provider Wiring', detail: 'Auth claims and notification providers are mounted for this view.' },
        ],
      }
  }
}

export const OwnerNotBuiltYet = ({ route }: { route: OwnerRoute }) => {
  const { t } = useNls()
  const spec = getRouteSpec(route)

  return (
    <div className="ownerNotBuiltYet">
      <div className="ownerNotBuiltYetCard">
        <div className="ownerNotBuiltYetHeader">
          <div>
            <span className="ownerEyebrow">{t('NLSOwnerConsoleNotBuiltYet')}</span>
            <h2>{spec.title}</h2>
            <p>{spec.description}</p>
          </div>
          <span className="ownerNotBuiltChapterBadge">{spec.chapter}</span>
        </div>

        <div className="ownerNotBuiltScaffolding">
          {spec.plannedFeatures.map((feature, idx) => (
            <div key={idx} className="ownerNotBuiltBlock">
              <h4>{feature.label}</h4>
              <p>{feature.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
